import { useT } from "../../../shared/i18n/i18n";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import type { FormEvent } from "react";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { WorkObjectFieldValueEditor } from "../../../entities/work-object/ui/work-object-field-editor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// The field subset an intake form can render — mirrors the server's
// public-form allowlist (no relations, people or computed fields).
export type IntakeField = {
  // The full definition when the form runs in-app; the public page builds a
  // minimal stand-in from the anonymous payload instead.
  definition?: WorkObjectFieldDefinition;
  id: string;
  name: string;
  options: Array<{ color: string; id: string; value: string }>;
  ratingScale?: number | null;
  required: boolean;
  type: string;
};

export type IntakeSubmission = {
  title: string;
  values: Array<{ fieldDefinitionId: string; value: string | string[] }>;
};

const multiValueTypes = new Set(["date_range", "multi_select"]);

function isEmptyDraft(value: string | string[]): boolean {
  return Array.isArray(value) ? value.every((entry) => entry.trim().length === 0) : value.trim().length === 0;
}

// The intake form renders the SAME field editors the task card uses — one
// component per field type across the whole product. The editor only needs a
// definition-shaped object, so the anonymous payload is adapted on the fly.
function editorField(field: IntakeField): WorkObjectFieldDefinition {
  if (field.definition) return field.definition;
  return {
    config: field.ratingScale ? { ratingScale: field.ratingScale } : {},
    id: field.id,
    key: field.id,
    name: field.name,
    options: field.options,
    type: field.type,
  } as unknown as WorkObjectFieldDefinition;
}

// Intake form shared by the in-app form view and the public capability page.
export function IntakeForm({
  fields,
  onSubmit,
  submitLabel,
  titlePlaceholder,
}: {
  fields: IntakeField[];
  onSubmit: (input: IntakeSubmission) => Promise<void>;
  submitLabel?: string;
  titlePlaceholder?: string;
}) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string | string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function draftFor(field: IntakeField): string | string[] {
    return drafts[field.id] ?? (multiValueTypes.has(field.type) ? [] : "");
  }

  function setDraft(fieldId: string, value: string | string[]) {
    setDrafts((current) => ({ ...current, [fieldId]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (title.trim().length < 2) {
      setError("Add a title — at least two characters.");
      return;
    }
    for (const field of fields) {
      if (field.required && isEmptyDraft(draftFor(field))) {
        setError(`“${field.name}” is required.`);
        return;
      }
      if (field.type === "date_range") {
        const range = draftFor(field);
        if (Array.isArray(range) && range.some((entry) => entry.length > 0) && range.filter((entry) => entry.length > 0).length !== 2) {
          setError(`“${field.name}” needs both a start and an end date.`);
          return;
        }
      }
    }

    const values = fields.flatMap((field) => {
      const draft = draftFor(field);
      if (isEmptyDraft(draft)) return [];
      return [{ fieldDefinitionId: field.id, value: draft }];
    });

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({ title: title.trim(), values });
      setTitle("");
      setDrafts({});
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not submit the form.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="flex w-full flex-col gap-4" onSubmit={submit}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="intake-title">
          {t("item.title")}
          <span className="text-destructive"> *</span>
        </Label>
        <Input
          autoFocus
          id="intake-title"
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={titlePlaceholder ?? t("forms.titlePlaceholder")}
          value={title}
        />
      </div>

      {fields.map((field) => (
        <div className="flex flex-col gap-1.5" key={field.id}>
          <Label>
            {field.name}
            {field.required ? <span className="text-destructive"> *</span> : null}
          </Label>
          {/* The exact editors the task card uses — the "form" surface swaps their ghost
              chrome for the standard bordered input look. */}
          <WorkObjectFieldValueEditor
            disabled={isSubmitting}
            field={editorField(field)}
            onCommit={(value) => setDraft(field.id, value)}
            surface="form"
            value={draftFor(field)}
          />
        </div>
      ))}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Button className="self-start" disabled={isSubmitting} type="submit">
        {isSubmitting ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
        {submitLabel ?? t("forms.submit")}
      </Button>
    </form>
  );
}

// Client-side mirror of the server's public-form field allowlist.
export const intakeFieldTypes = new Set([
  "checkbox",
  "currency",
  "date",
  "date_range",
  "duration",
  "email",
  "multi_select",
  "number",
  "percent",
  "phone",
  "rating",
  "select",
  "text",
  "textarea",
  "url",
]);
